const DB_NAME = 'memberNest.offlineMedia';
const DB_VERSION = 1;
const STORE_NAME = 'media';
let maxCacheBytes = 100 * 1024 * 1024;
let maxCacheAgeMs = 14 * 24 * 60 * 60 * 1000;

const memoryStore = new Map();

function now() {
  return Date.now();
}

function hasIndexedDb() {
  return typeof indexedDB !== 'undefined';
}

export function getOfflineMediaCacheKey(userId, storagePath) {
  return `${encodeURIComponent(String(userId || ''))}:${encodeURIComponent(String(storagePath || ''))}`;
}

function isUsableInput(userId, storagePath) {
  return Boolean(userId && storagePath && String(storagePath).startsWith('media-uploads/approved/'));
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed.'));
  });
}

let dbPromise = null;

function openDb() {
  if (!hasIndexedDb()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      const store = db.objectStoreNames.contains(STORE_NAME)
        ? request.transaction.objectStore(STORE_NAME)
        : db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      if (!store.indexNames.contains('userId')) store.createIndex('userId', 'userId', { unique: false });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Could not open offline media cache.'));
  }).catch((error) => {
    dbPromise = null;
    throw error;
  });
  return dbPromise;
}

async function withStore(mode, callback) {
  const db = await openDb();
  if (!db) return callback(null);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    let result;
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error || new Error('Offline media cache transaction failed.'));
    transaction.onabort = () => reject(transaction.error || new Error('Offline media cache transaction aborted.'));
    Promise.resolve(callback(store)).then((value) => {
      result = value;
    }).catch((error) => {
      transaction.abort();
      reject(error);
    });
  });
}

function isExpired(record, at = now()) {
  return !record?.updatedAt || at - Number(record.updatedAt) > maxCacheAgeMs;
}

function normalizeRecord({ userId, storagePath, blob, contentType, mediaType }) {
  const at = now();
  return {
    id: getOfflineMediaCacheKey(userId, storagePath),
    userId,
    storagePath,
    blob,
    contentType: contentType || blob?.type || 'application/octet-stream',
    mediaType: mediaType || 'image',
    size: Number(blob?.size || 0),
    createdAt: at,
    updatedAt: at,
    lastAccessedAt: at,
  };
}

function getMemoryRecordsForUser(userId) {
  return Array.from(memoryStore.values()).filter((record) => record.userId === userId);
}

async function getRecordsForUser(userId) {
  if (!hasIndexedDb()) return getMemoryRecordsForUser(userId);
  return withStore('readonly', (store) => requestToPromise(store.index('userId').getAll(userId)));
}

async function deleteRecords(ids = []) {
  if (ids.length === 0) return;
  if (!hasIndexedDb()) {
    ids.forEach((id) => memoryStore.delete(id));
    return;
  }
  await withStore('readwrite', (store) => {
    ids.forEach((id) => store.delete(id));
  });
}

async function enforceUserLimits(userId) {
  const at = now();
  const records = await getRecordsForUser(userId);
  const expiredIds = records.filter((record) => isExpired(record, at)).map((record) => record.id);
  const activeRecords = records.filter((record) => !expiredIds.includes(record.id));
  activeRecords.sort((left, right) => Number(left.lastAccessedAt || 0) - Number(right.lastAccessedAt || 0));

  let totalBytes = activeRecords.reduce((sum, record) => sum + Number(record.size || record.blob?.size || 0), 0);
  const deleteIds = [...expiredIds];
  for (const record of activeRecords) {
    if (totalBytes <= maxCacheBytes) break;
    deleteIds.push(record.id);
    totalBytes -= Number(record.size || record.blob?.size || 0);
  }
  await deleteRecords(deleteIds);
}

export async function readOfflineMediaCache({ userId, storagePath }) {
  if (!isUsableInput(userId, storagePath)) return null;
  const id = getOfflineMediaCacheKey(userId, storagePath);
  const at = now();

  if (!hasIndexedDb()) {
    const record = memoryStore.get(id);
    if (!record) return null;
    if (isExpired(record, at)) {
      memoryStore.delete(id);
      return null;
    }
    record.lastAccessedAt = at;
    return record;
  }

  return withStore('readwrite', async (store) => {
    const record = await requestToPromise(store.get(id));
    if (!record) return null;
    if (isExpired(record, at)) {
      store.delete(id);
      return null;
    }
    const nextRecord = { ...record, lastAccessedAt: at };
    store.put(nextRecord);
    return nextRecord;
  });
}

export async function writeOfflineMediaCache({ userId, storagePath, blob, contentType, mediaType }) {
  if (!isUsableInput(userId, storagePath) || !(blob instanceof Blob) || blob.size <= 0) return false;
  const record = normalizeRecord({ userId, storagePath, blob, contentType, mediaType });
  if (record.size > maxCacheBytes) return false;

  if (!hasIndexedDb()) {
    memoryStore.set(record.id, record);
    await enforceUserLimits(userId);
    return true;
  }

  await withStore('readwrite', (store) => {
    store.put(record);
  });
  await enforceUserLimits(userId);
  return true;
}

export async function clearOfflineMediaCacheForUser(userId) {
  if (!userId) return;
  const records = await getRecordsForUser(userId);
  await deleteRecords(records.map((record) => record.id));
}

export async function clearAllOfflineMediaCache() {
  if (!hasIndexedDb()) {
    memoryStore.clear();
    return;
  }
  await withStore('readwrite', (store) => {
    store.clear();
  });
}

export const OFFLINE_MEDIA_CACHE_LIMITS = {
  maxBytes: 100 * 1024 * 1024,
  maxAgeMs: 14 * 24 * 60 * 60 * 1000,
};

export function configureOfflineMediaCacheForTests({ maxBytes, maxAgeMs } = {}) {
  if (process.env.NODE_ENV !== 'test') return;
  maxCacheBytes = Number(maxBytes || 0) || OFFLINE_MEDIA_CACHE_LIMITS.maxBytes;
  maxCacheAgeMs = Number(maxAgeMs || 0) || OFFLINE_MEDIA_CACHE_LIMITS.maxAgeMs;
}
